import { Fragment, useMemo, useState } from "react"
import { DropdownItem, DropdownMenu, DropdownToggle, Table, UncontrolledDropdown } from "reactstrap"
import ActionButton from "Components/atoms/buttons/action-button"
import IconButton from "Components/atoms/buttons/icon-button"
import { DotsVerticalIcon, PencilIcon, PlusIcon, TrashIcon } from "Components/atoms/icons"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import VendorFormModal from "./vendor-form-modal"
import ProductFormModal from "./product-form-modal"
import {
    BillingFrequency, ExpenseBudget, ExpenseBudgetLine, ExpenseBudgetLinePayload, ExpenseBudgetVendor, VendorPayload,
} from "models/expense-budget"
import {
    createExpenseBudgetLine, createExpenseBudgetVendor, deleteExpenseBudgetLine, deleteExpenseBudgetVendor,
    updateExpenseBudgetLine, updateExpenseBudgetVendor,
} from "slices/finance/expense-budget/thunk"
import { isAmortizedFrequency, lineAnnualCad, sumAmounts, toPercent } from "utils/expense-budget"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { formatCurrency } from "utils/tools"

interface LinesTabProps {
    budget: ExpenseBudget
    lines: ExpenseBudgetLine[]
    onBudgetChanged: () => Promise<void>
    onLinesChanged: () => Promise<void>
}

type Deleting = { kind: "vendor"; vendor: ExpenseBudgetVendor } | { kind: "product"; line: ExpenseBudgetLine }

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

const FREQUENCY_LABELS: Record<BillingFrequency, string> = {
    [BillingFrequency.Monthly]: "Monthly",
    [BillingFrequency.Quarterly]: "Quarterly",
    [BillingFrequency.Annually]: "Annually",
    [BillingFrequency.OneTime]: "One time",
}

const billedLabel = (line: ExpenseBudgetLine): string => {
    if (line.renewalDate) return `Renews ${line.renewalDate.slice(0, 10)}`
    if (line.billingFrequency === BillingFrequency.Monthly) return "-"
    return calendar.periodToMonth(line.firstBillingPeriod % 100, "en-CA", true)
}

// The budget as in the workbook: each vendor with its total, then its products.
// Cost codes are assigned later in the Mapping tab.
const LinesTab = ({ budget, lines, onBudgetChanged, onLinesChanged }: LinesTabProps) => {
    const [vendorForm, setVendorForm] = useState<{ vendor: ExpenseBudgetVendor | null } | null>(null)
    const [productForm, setProductForm] = useState<{ line: ExpenseBudgetLine | null; vendorId: string | null } | null>(null)
    const [deleting, setDeleting] = useState<Deleting | null>(null)
    const [isSaving, setIsSaving] = useState(false)

    const vendorGroups = useMemo(() => budget.vendors.map(vendor => {
        const vendorLines = lines.filter(line => line.expenseBudgetVendorId === vendor.id)
        return { vendor, lines: vendorLines, annualCad: sumAmounts(vendorLines.map(lineAnnualCad)) }
    }), [budget.vendors, lines])

    const costCodeLabel = (costCodeId: string | null) =>
        budget.costCodes.find(c => c.id === costCodeId)?.transactionAccount.code.trim() ?? "Unmapped"

    const handleSaveVendor = async (payload: VendorPayload) => {
        const editing = vendorForm?.vendor
        setIsSaving(true)
        const saved = editing
            ? await updateExpenseBudgetVendor(budget.id, editing.id, payload)
            : await createExpenseBudgetVendor(budget.id, payload)
        setIsSaving(false)
        if (!saved) return
        setVendorForm(null)
        await onBudgetChanged()
        if (editing) await onLinesChanged()
    }

    // Vendors added from inside the product form.
    const handleCreateVendor = async (name: string) => {
        const vendor = await createExpenseBudgetVendor(budget.id, { name })
        if (vendor) await onBudgetChanged()
        return vendor
    }

    const handleSaveProduct = async (payload: ExpenseBudgetLinePayload) => {
        const editing = productForm?.line
        setIsSaving(true)
        const saved = editing
            ? await updateExpenseBudgetLine(budget.id, editing.id, payload)
            : await createExpenseBudgetLine(budget.id, payload)
        setIsSaving(false)
        if (!saved) return
        setProductForm(null)
        await onLinesChanged()
    }

    const handleDelete = async () => {
        if (!deleting) return
        const isDeleted = deleting.kind === "vendor"
            ? await deleteExpenseBudgetVendor(budget.id, deleting.vendor.id)
            : await deleteExpenseBudgetLine(budget.id, deleting.line.id)
        setDeleting(null)
        if (!isDeleted) return
        if (deleting.kind === "vendor") await onBudgetChanged()
        else await onLinesChanged()
    }

    const deletingTitle = deleting?.kind === "vendor" ? "Delete vendor?" : "Delete product?"
    const deletingSubtitle = deleting?.kind === "vendor"
        ? deleting.vendor.name
        : deleting ? `${deleting.line.vendor.name} – ${deleting.line.productName}` : ""

    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <span className="text-muted">
                    {budget.vendors.length} vendors · {lines.length} products · {formatCurrency(sumAmounts(lines.map(lineAnnualCad)))} planned (CAD, tax incl.)
                </span>
                <div className="d-flex gap-2">
                    <ActionButton onClick={() => setVendorForm({ vendor: null })}>Add Vendor <PlusIcon /></ActionButton>
                    <ActionButton disabled={budget.vendors.length === 0} onClick={() => setProductForm({ line: null, vendorId: null })}>
                        Add Product <PlusIcon />
                    </ActionButton>
                </div>
            </div>
            <div style={{ overflowX: "auto" }}>
                <Table size="sm" hover className="align-middle">
                    <thead>
                        <tr>
                            <th>Vendor / Product</th><th>Cost Code</th><th className="text-end">Qty</th>
                            <th className="text-end">Unit Price</th><th className="text-end">Tax</th><th>Billing</th>
                            <th>Renewal / Bill</th><th className="text-end">Annual (CAD)</th><th />
                        </tr>
                    </thead>
                    <tbody>
                        {vendorGroups.map(group => (
                            <Fragment key={group.vendor.id}>
                                <tr style={{ background: "var(--bs-secondary-bg)", fontWeight: 600 }}>
                                    <td colSpan={7}>
                                        {group.vendor.name}
                                        {group.vendor.codes.length > 0 &&
                                            <span className="text-muted small fw-normal ms-2">
                                                Acumatica: {group.vendor.codes.map(code => code.vendorCode).join(", ")}
                                            </span>}
                                    </td>
                                    <td className="text-end">{formatCurrency(group.annualCad)}</td>
                                    <td className="text-end">
                                        <UncontrolledDropdown>
                                            <DropdownToggle tag="button" className="icon-button"><DotsVerticalIcon /></DropdownToggle>
                                            <DropdownMenu end>
                                                <DropdownItem onClick={() => setProductForm({ line: null, vendorId: group.vendor.id })}>
                                                    <PlusIcon /> Add product
                                                </DropdownItem>
                                                <DropdownItem onClick={() => setVendorForm({ vendor: group.vendor })}><PencilIcon /> Edit vendor</DropdownItem>
                                                <DropdownItem className="text-danger" onClick={() => setDeleting({ kind: "vendor", vendor: group.vendor })}>
                                                    <TrashIcon /> Delete vendor
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </UncontrolledDropdown>
                                    </td>
                                </tr>
                                {group.lines.length === 0 && (
                                    <tr><td colSpan={9} className="text-muted" style={{ paddingLeft: 24 }}>No products yet — use + to add one.</td></tr>
                                )}
                                {group.lines.map(line => (
                                    <tr key={line.id}>
                                        <td style={{ paddingLeft: 24 }}>{line.productName}</td>
                                        <td>{costCodeLabel(line.expenseBudgetCostCodeId)}</td>
                                        <td className="text-end">{Number(line.quantity)}</td>
                                        <td className="text-end">{formatCurrency(line.unitPrice, line.currency)}</td>
                                        <td className="text-end">{toPercent(line.taxRate)}%</td>
                                        <td>{FREQUENCY_LABELS[line.billingFrequency]}{isAmortizedFrequency(line.billingFrequency) ? " · amortized" : ""}</td>
                                        <td>{billedLabel(line)}</td>
                                        <td className="text-end">{formatCurrency(lineAnnualCad(line))}</td>
                                        <td>
                                            <div className="d-flex gap-1 justify-content-end">
                                                <IconButton onClick={() => setProductForm({ line, vendorId: line.expenseBudgetVendorId })}><PencilIcon /></IconButton>
                                                <IconButton onClick={() => setDeleting({ kind: "product", line })}><TrashIcon /></IconButton>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </Fragment>
                        ))}
                    </tbody>
                </Table>
            </div>
            <VendorFormModal isOpen={!!vendorForm} vendor={vendorForm?.vendor ?? null} isSaving={isSaving}
                onClose={() => setVendorForm(null)} onSave={handleSaveVendor} />
            <ProductFormModal isOpen={!!productForm} budget={budget} line={productForm?.line ?? null}
                defaultVendorId={productForm?.vendorId ?? null} isSaving={isSaving}
                onClose={() => setProductForm(null)} onSave={handleSaveProduct} onCreateVendor={handleCreateVendor} />
            <AreYouSureModal isOpen={!!deleting} onModalClose={() => setDeleting(null)}
                modalTitle={deletingTitle} subTitle={deletingSubtitle}
                areYouSureacceptText="Delete" areYouSuredeclineText="Cancel"
                handleAreYouSureAccept={handleDelete} handleAreYouSureDecline={async () => setDeleting(null)} />
        </div>
    )
}

export default LinesTab
